/**
 * Shared ApexCharts machinery for dashboard chart factories (reviews,
 * analytics, ...). Theme-aware: reads DaisyUI CSS custom properties at
 * runtime so charts adapt to both dark and light modes.
 *
 * Pure generic helpers only — chart-type-specific factories live in their
 * own modules (e.g. `review-charts.ts`) and import from here.
 */

// ---------------------------------------------------------------------------
// Theme-aware helpers
// ---------------------------------------------------------------------------

/**
 * Resolve any CSS color to a hex string via canvas getImageData.
 * This is the only reliable cross-browser method for oklch → sRGB conversion.
 * Canvas rasterization always outputs sRGB pixels regardless of input color space.
 */
export function resolveColor(cssColor: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = cssColor;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

/** Append hex alpha to a hex color: "#c5785c" + 0.4 → "#c5785c66" */
export function hexAlpha(hex: string, alpha: number): string {
  return (
    hex +
    Math.round(alpha * 255)
      .toString(16)
      .padStart(2, "0")
  );
}

/** Get theme colors resolved to hex at runtime. */
export function themeColors() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();

  const secondary = resolveColor(`oklch(${v("--s")})`); // copper accent
  const textColor = resolveColor(`oklch(${v("--bc")})`); // base content (text)
  const bgColor = resolveColor(`oklch(${v("--b1")})`); // base background
  const gridColor = resolveColor(`oklch(${v("--b3")})`); // subtle border
  const mutedText = hexAlpha(textColor, 0.55); // text at 55% opacity
  const success = resolveColor(`oklch(${v("--su")})`);
  const error = resolveColor(`oklch(${v("--er")})`);

  return { secondary, textColor, bgColor, gridColor, mutedText, success, error };
}

/** Resolve once ApexCharts is available on window (on-demand vendor load). */
export function waitForApexCharts(): Promise<void> {
  if ((window as any).ApexCharts) return Promise.resolve();
  return new Promise((resolve) => {
    const s = document.querySelector('script[src*="apexcharts"]');
    if (s) {
      s.addEventListener("load", () => resolve());
    } else {
      const check = setInterval(() => {
        if ((window as any).ApexCharts) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    }
  });
}

/** Wait until element is visible (has non-zero dimensions), then wait for layout settle. */
export function waitForVisible(el: HTMLElement | undefined | null): Promise<void> {
  const waitFrame = () =>
    new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  // el may be undefined/null or detached: a queued render fn captured a `self`
  // whose component was re-initialized or swapped away before the fn ran (an
  // HTMX self-refresh while a prior render was still queued), leaving its $refs
  // empty/stale. Resolve immediately; the caller's post-await `?.isConnected`
  // guard then skips rendering into a dead/missing node. (Reading `.isConnected`
  // on `undefined` here would throw → poison the shared renderQueue → every
  // subsequent chart fails to render.)
  if (!el || !el.isConnected) return Promise.resolve();
  if (el.offsetWidth > 0 && el.offsetHeight > 0) return waitFrame();
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      waitFrame().then(resolve);
    };
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) finish();
    });
    observer.observe(el);
    // Never stall the shared renderQueue: resolve if the node is detached
    // (removed by an HTMX swap) or after a timeout even if never visible.
    const timer = setTimeout(finish, 4000);
  });
}

// Serialize chart renders to prevent layout thrashing. A failed render is
// isolated (caught) so it can NEVER reject renderQueue — a rejected queue would
// make every subsequent `.then(fn)` skip its fn, breaking all later charts. One
// chart failing must not take the others down.
let renderQueue: Promise<void> = Promise.resolve();
export function enqueueRender(fn: () => Promise<void>): Promise<void> {
  renderQueue = renderQueue.then(() =>
    fn().catch((e) => {
      // eslint-disable-next-line no-console
      console.warn("[chart-core] render skipped:", e);
    }),
  );
  return renderQueue;
}

// ---------------------------------------------------------------------------
// Theme-change subscription
// ---------------------------------------------------------------------------

// Charts bake their theme colors (line/legend/axis-tick/dataLabel colors +
// the light/dark tooltip theme) into the ApexCharts instance at construction
// time — see themeColors()/baseOpts(). A live light/dark toggle only flips the
// <html data-theme> attribute, so an already-rendered chart keeps its old
// colors until it is rebuilt. onThemeChange lets a chart subscribe to that
// attribute flip; themeAware (below) uses it to rebuild on toggle.
const themeChangeListeners = new Set<() => void>();
let themeObserver: MutationObserver | null = null;

/**
 * Register a callback fired whenever the app theme (the `data-theme` attribute
 * on <html>) changes. Backed by ONE shared MutationObserver — every theme
 * source (the floating toggle, the sidebar control, the CMS editor, the
 * pre-paint set) mutates that attribute, so observing it covers them all
 * without each producer having to dispatch an event. Returns an unsubscribe fn
 * — call it on component teardown so a chart swapped away by HTMX doesn't leak
 * a listener (and doesn't try to re-theme a destroyed instance).
 */
export function onThemeChange(cb: () => void): () => void {
  themeChangeListeners.add(cb);
  if (!themeObserver) {
    themeObserver = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.attributeName === "data-theme")) {
        // Isolate each callback: one throwing must not skip the rest.
        themeChangeListeners.forEach((fn) => {
          try {
            fn();
          } catch (e) {
            console.warn("[chart-core] theme listener failed:", e);
          }
        });
      }
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }
  return () => {
    themeChangeListeners.delete(cb);
  };
}

/**
 * Decorate an Alpine chart component so it rebuilds on theme change. Wrap the
 * object a factory returns: `return themeAware({ chart: null, init() {...},
 * destroy() {...} })`. On a `data-theme` flip it calls the component's own
 * destroy() then init() (bound to the live Alpine proxy, so `this.$refs` stays
 * valid) — rebuilding the ApexCharts instance(s) with freshly-resolved theme
 * colors. The theme listener is registered once (guarded by _themeBound) and
 * released in destroy(), which x-destroy invokes on HTMX teardown. Shape-
 * agnostic: it only calls the component's existing init/destroy, so a factory
 * that manages one chart or several (e.g. own+comp donuts) is handled by its
 * own destroy().
 */
export function themeAware<
  T extends { init: (...a: any[]) => any; destroy?: (...a: any[]) => any },
>(comp: T): T {
  const c = comp as any;
  const origInit = c.init;
  const origDestroy = c.destroy;
  c.init = function (this: any, ...args: any[]) {
    if (!this._themeBound) {
      this._themeBound = true;
      this._unsubTheme = onThemeChange(() => {
        origDestroy?.call(this);
        origInit.call(this);
      });
    }
    return origInit.call(this, ...args);
  };
  c.destroy = function (this: any, ...args: any[]) {
    this._unsubTheme?.();
    origDestroy?.call(this, ...args);
  };
  return comp;
}

/**
 * Clear a chart mount before (re-)rendering into it. Call at the top of every
 * render closure, right after the isConnected guard.
 *
 * Guards the init/theme-change race: init() queues an ASYNC render but does not
 * set this.chart synchronously, so a `data-theme` mutation firing in that gap
 * runs the themeAware callback's origDestroy() (a no-op — this.chart is still
 * undefined) then queues a SECOND render. Both renders then paint into the same
 * mount, and the first chart — its reference overwritten, its destroy() never
 * called — lingers as an orphaned, overlapping SVG (doubled axis labels,
 * garbled overlapping text incl. umlaut labels). Because enqueueRender is
 * serial, the second render sees this.chart set by the first, so destroying it
 * and emptying the node makes every render start from a clean mount.
 */
export function resetChartMount(self: any): void {
  try {
    self.chart?.destroy();
  } catch {
    /* already destroyed / never rendered */
  }
  self.chart = null;
  self.$refs?.chart?.replaceChildren();
}

/** Build base options that adapt to the current theme. */
export function baseOpts() {
  const t = themeColors();
  return {
    chart: {
      background: "transparent",
      fontFamily: "inherit",
      toolbar: { show: false },
    },
    grid: {
      borderColor: hexAlpha(t.gridColor, 0.25),
      strokeDashArray: 3,
    },
    tooltip: {
      theme: document.documentElement.getAttribute("data-theme")?.includes("light")
        ? "light"
        : "dark",
      style: { fontSize: "12px" },
    },
    title: { text: "", style: { color: t.textColor, fontSize: "0px" } },
    dataLabels: { enabled: false },
  };
}
